import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckSquare, Square, Wallet, Receipt, ArrowRight, Zap, DollarSign, MoveHorizontal, Undo2 } from "lucide-react";

import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";

/**
 * Iter 66 — Receive customer payment (batch).
 */
export default function ReceivePayment() {
  const [currency, setCurrency] = useState("USD");
  const [customers, setCustomers] = useState([]);
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [openInvoices, setOpenInvoices] = useState([]);
  const [checked, setChecked] = useState({});
  const [total, setTotal] = useState("");
  const [depositCode, setDepositCode] = useState("1050");
  const [method, setMethod] = useState("bank_transfer");
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState("");
  const [saving, setSaving] = useState(false);
  const [reallocating, setReallocating] = useState(null); // batch id being reallocated
  const [reallocateCode, setReallocateCode] = useState("1000");
  const [busyBatch, setBusyBatch] = useState(null);

  const reallocateBatch = async (batchId) => {
    setBusyBatch(batchId);
    try {
      const { data } = await api.post(`/payments/batches/${batchId}/reallocate`,
        { new_account_code: reallocateCode });
      toast.success(`Moved ${formatCurrency(data.amount, currency)} from ${data.from_code} → ${data.to_code}`);
      setReallocating(null);
      loadStatic();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reallocation failed");
    } finally {
      setBusyBatch(null);
    }
  };

  const reverseBatch = async (batchId) => {
    if (!window.confirm("Reverse this payment? Invoices will re-open and a mirror JE will be posted.")) return;
    setBusyBatch(batchId);
    try {
      const { data } = await api.post(`/payments/batches/${batchId}/reverse`);
      toast.success(`Reversed · ${data.reopened.length} invoice${data.reopened.length === 1 ? "" : "s"} re-opened`);
      loadStatic();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reverse failed");
    } finally {
      setBusyBatch(null);
    }
  };

  const loadStatic = useCallback(async () => {
    try {
      const [c, d, b] = await Promise.all([
        api.get("/customers"),
        api.get("/payments/deposit-accounts"),
        api.get("/payments/batches?kind=customer_payment&limit=5"),
      ]);
      setCustomers(c.data);
      setDepositAccounts(d.data);
      setBatches(b.data);
      const def = d.data.find((a) => a.is_default);
      if (def) setDepositCode(def.code);
    } catch {
      toast.error("Failed to load initial data");
    }
  }, []);
  useEffect(() => { loadStatic(); }, [loadStatic]);

  const loadOpen = useCallback(async () => {
    if (!customerId) { setOpenInvoices([]); return; }
    try {
      const { data } = await api.get(`/payments/customer/open?customer_id=${customerId}&currency=${currency}`);
      setOpenInvoices(data);
      // Pre-check everything by default so the batch UX is 1-click
      const preset = {};
      data.forEach((inv) => { preset[inv.id] = true; });
      setChecked(preset);
    } catch {
      toast.error("Failed to load open invoices");
    }
  }, [customerId, currency]);
  useEffect(() => { loadOpen(); }, [loadOpen]);

  const selected = useMemo(
    () => openInvoices.filter((inv) => checked[inv.id]),
    [openInvoices, checked]
  );
  const selectedTotal = useMemo(
    () => selected.reduce((s, inv) => s + inv.outstanding, 0),
    [selected]
  );

  const applyFullAmount = () => setTotal(selectedTotal.toFixed(2));

  const toggleAll = () => {
    if (openInvoices.every((inv) => checked[inv.id])) {
      setChecked({});
    } else {
      const preset = {};
      openInvoices.forEach((inv) => { preset[inv.id] = true; });
      setChecked(preset);
    }
  };

  const submit = async () => {
    const amt = parseFloat(total);
    if (!customerId || !amt || amt <= 0) return;
    if (!selected.length) {
      toast.error("Select at least one invoice");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/payments/customer", {
        customer_id: customerId,
        total_amount: amt,
        date: dateStr,
        currency,
        deposit_account_code: depositCode,
        method,
        memo,
        // Backend auto-allocates oldest-first across the checked invoices when
        // we don't send allocations — but we still want to restrict which
        // invoices are candidates. Sort chosen invoices by date and pass
        // explicit allocations sized oldest-first.
        allocations: (() => {
          const ordered = [...selected].sort((a, b) => a.date.localeCompare(b.date));
          let remaining = amt;
          const out = [];
          for (const inv of ordered) {
            if (remaining <= 0.005) break;
            const take = Math.min(remaining, inv.outstanding);
            out.push({ invoice_id: inv.id, amount: parseFloat(take.toFixed(2)) });
            remaining -= take;
          }
          if (remaining > 0.005 && out.length) {
            // Residual pins to last (customer overpaid)
            out[out.length - 1].amount = parseFloat((out[out.length - 1].amount + remaining).toFixed(2));
          }
          return out;
        })(),
      });
      toast.success(`Received ${formatCurrency(amt, currency)} · ${data.applied.length} invoice${data.applied.length === 1 ? "" : "s"} paid`);
      setTotal(""); setMemo("");
      loadOpen();
      loadStatic();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
    } finally {
      setSaving(false);
    }
  };

  const currencySym = currency === "NGN" ? "₦" : "$";

  return (
    <AppShell>
      <div className="space-y-6" data-testid="receive-payment-page">
        <header className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Accounts Receivable</div>
            <h1 className="font-display text-3xl">Receive customer payment</h1>
            <p className="text-sm text-[var(--muted-text)] mt-1">
              Log a payment from a customer and auto-apply it across open invoices.
              Posts a balanced journal entry so your books stay accurate.
            </p>
          </div>
          <div className="w-11 h-11 rounded-lg bg-[#EDF3EF] text-[#4C956C] flex items-center justify-center">
            <Receipt className="w-5 h-5" />
          </div>
        </header>

        {/* Step 1 · Customer */}
        <div className="card-flat p-5">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] mb-3">Step 1 · Who paid you</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Customer</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="rp-customer" className="mt-1"><SelectValue placeholder="Choose customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="rp-currency" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD · $</SelectItem>
                  <SelectItem value="NGN">NGN · ₦</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date received</Label>
              <Input data-testid="rp-date" type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="rp-method" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Step 2 · Invoices */}
        {customerId && (
          <div className="card-flat p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Step 2 · Apply to invoices</div>
                <p className="text-sm text-[var(--muted-text)] mt-1">
                  {openInvoices.length ? `${openInvoices.length} open invoice${openInvoices.length === 1 ? "" : "s"} · ${currency}` : `No open ${currency} invoices for this customer`}
                </p>
              </div>
              {openInvoices.length > 0 && (
                <Button variant="ghost" size="sm" onClick={toggleAll} data-testid="rp-toggle-all">
                  {openInvoices.every((inv) => checked[inv.id]) ? "Uncheck all" : "Check all"}
                </Button>
              )}
            </div>
            {openInvoices.length > 0 ? (
              <div className="rounded-md border border-[var(--divider)] overflow-hidden">
                <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-wider bg-[var(--hover)] text-[var(--muted-text)]">
                  <div className="col-span-1"></div>
                  <div className="col-span-3">Number</div>
                  <div className="col-span-2">Due</div>
                  <div className="col-span-3">Status</div>
                  <div className="col-span-3 text-right">Outstanding</div>
                </div>
                {openInvoices.map((inv) => (
                  <div key={inv.id} className="grid grid-cols-12 px-3 py-2 text-sm border-t border-[var(--divider)] items-center hover:bg-[var(--row-hover)]">
                    <div className="col-span-1">
                      <button
                        data-testid={`rp-check-${inv.id}`}
                        onClick={() => setChecked({ ...checked, [inv.id]: !checked[inv.id] })}
                        className="text-[var(--brand)]"
                      >
                        {checked[inv.id] ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="col-span-3 font-mono-data">{inv.number}</div>
                    <div className="col-span-2 text-xs text-[var(--muted-text)]">{inv.due_date?.slice(0, 10)}</div>
                    <div className="col-span-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${inv.status === "overdue" ? "bg-[#FBEEE7] text-[#B4283A]" : "bg-[#F5F1E9] text-[#7A6C4E]"}`}>{inv.status}</span>
                    </div>
                    <div className="col-span-3 text-right font-mono-data">{formatCurrency(inv.outstanding, inv.currency)}</div>
                  </div>
                ))}
                <div className="grid grid-cols-12 px-3 py-2 text-sm border-t border-[var(--divider)] bg-[#FBF9F3]/60 font-medium">
                  <div className="col-span-9">Selected outstanding</div>
                  <div className="col-span-3 text-right font-mono-data">{formatCurrency(selectedTotal, currency)}</div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-[var(--muted-text)] py-4">Nothing to pay — this customer has no open {currency} invoices.</div>
            )}
          </div>
        )}

        {/* Step 3 · Deposit + total */}
        {customerId && openInvoices.length > 0 && (
          <div className="card-flat p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] mb-3">Step 3 · Where the money lands</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Deposit to</Label>
                <Select value={depositCode} onValueChange={setDepositCode}>
                  <SelectTrigger data-testid="rp-deposit" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {depositAccounts.map((a) => (
                      <SelectItem key={a.code} value={a.code}>
                        {a.code} · {a.name}{a.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount received</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-text)]">{currencySym}</span>
                  <Input
                    data-testid="rp-amount" type="number" step="0.01" min="0"
                    value={total} onChange={(e) => setTotal(e.target.value)}
                    className="pl-7" placeholder="0.00"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={applyFullAmount} data-testid="rp-fill-total" className="gap-1.5">
                  <Zap className="w-3.5 h-3.5" /> Use selected total ({formatCurrency(selectedTotal, currency)})
                </Button>
              </div>
            </div>
            <div className="mt-4">
              <Label>Memo (optional)</Label>
              <Textarea
                data-testid="rp-memo" value={memo} onChange={(e) => setMemo(e.target.value)}
                placeholder="Check #, wire ref, notes for the books…"
                rows={2} className="mt-1"
              />
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                data-testid="rp-submit"
                onClick={submit}
                disabled={saving || !total || parseFloat(total) <= 0 || !selected.length}
                className="btn-brand gap-1.5"
              >
                <DollarSign className="w-4 h-4" /> {saving ? "Posting…" : "Record payment"}
              </Button>
            </div>
          </div>
        )}

        {/* Recent receipts */}
        {batches.length > 0 && (
          <div className="card-flat p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] mb-3">Recent receipts</div>
            <div className="rounded-md border border-[var(--divider)] overflow-hidden">
              <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-wider bg-[var(--hover)] text-[var(--muted-text)]">
                <div className="col-span-2">Date</div>
                <div className="col-span-3">Customer</div>
                <div className="col-span-2">Deposit</div>
                <div className="col-span-2">Applied to</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-1"></div>
              </div>
              {batches.map((b) => {
                const isReversed = b.status === "reversed";
                const isUndeposited = b.deposit_account_code === "1050" && !isReversed;
                return (
                  <div key={b.id} data-testid={`rp-batch-${b.id}`} className="border-t border-[var(--divider)] first:border-t-0">
                    <div className={`grid grid-cols-12 px-3 py-2 text-sm ${isReversed ? "opacity-50 line-through" : ""}`}>
                      <div className="col-span-2 font-mono-data">{b.date}</div>
                      <div className="col-span-3">{b.party_name || "Customer"}</div>
                      <div className="col-span-2 text-xs">
                        {b.deposit_account_code} · {b.deposit_account_name}
                        <div className="text-[10px] text-[var(--muted-text)] mt-0.5">{b.method}</div>
                      </div>
                      <div className="col-span-2 text-xs text-[var(--muted-text)]">
                        {(b.applied || []).slice(0, 2).map((a) => a.number).join(", ")}
                        {(b.applied || []).length > 2 ? ` +${b.applied.length - 2}` : ""}
                      </div>
                      <div className="col-span-2 text-right font-mono-data">{formatCurrency(b.total, b.currency)}</div>
                      <div className="col-span-1 flex justify-end gap-1">
                        {!isReversed && isUndeposited && reallocating !== b.batch_id && (
                          <Button data-testid={`rp-reallocate-${b.batch_id}`} size="icon" variant="ghost"
                            title="Move to real bank account"
                            onClick={() => setReallocating(b.batch_id)}>
                            <MoveHorizontal className="w-4 h-4 text-[#2F4F4F]" />
                          </Button>
                        )}
                        {!isReversed && (
                          <Button data-testid={`rp-reverse-${b.batch_id}`} size="icon" variant="ghost"
                            title="Reverse this payment"
                            disabled={busyBatch === b.batch_id}
                            onClick={() => reverseBatch(b.batch_id)}>
                            <Undo2 className="w-4 h-4 text-[#B4283A]" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {reallocating === b.batch_id && (
                      <div data-testid={`rp-reallocate-panel-${b.batch_id}`} className="px-3 pb-3 pt-1 bg-[#F5EAE3]/40 flex items-center gap-3">
                        <span className="text-xs text-[var(--muted-text)]">Move to:</span>
                        <Select value={reallocateCode} onValueChange={setReallocateCode}>
                          <SelectTrigger data-testid={`rp-reallocate-select-${b.batch_id}`} className="h-8 w-64"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {depositAccounts.filter((a) => a.code !== "1050").map((a) => (
                              <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button data-testid={`rp-reallocate-confirm-${b.batch_id}`}
                          size="sm" className="btn-brand"
                          disabled={busyBatch === b.batch_id}
                          onClick={() => reallocateBatch(b.batch_id)}>
                          {busyBatch === b.batch_id ? "Moving…" : "Confirm move"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setReallocating(null)}>Cancel</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
